<script setup>
import { ref, computed, getCurrentInstance, watch } from 'vue';
import { router } from '@inertiajs/vue3';
import { useLoading } from '@/composables/useLoading';
import Divider from 'primevue/divider';
import RecipientContactBlock from '@/components/admin/orders/RecipientContactBlock.vue';
import PublicationEmailOptions from '@/components/admin/orders/PublicationEmailOptions.vue';
import SiteFeaturesInfo from '@/components/SiteFeaturesInfo.vue';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const { showLoading, hideLoading } = useLoading();

const props = defineProps({
    order: {
        type: Object,
        required: true
    },
    itemsWithContent: {
        type: Array,
        default: () => []
    },
    allSent: {
        type: Boolean,
        default: false
    },
    hasConfirmedContent: {
        type: Boolean,
        default: false
    },
    isEnabled: {
        type: Boolean,
        default: false
    },
    hideConfirmButton: {
        type: Boolean,
        default: false
    },
    stepNumber: {
        type: Number,
        default: 3
    },
    isCreatePage: {
        type: Boolean,
        default: false
    },
    isEditMode: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['publication-confirmed', 'refresh-needed', 'publication-date-changed']);

const publicationDates = ref({});
const expandedItems = ref({});
const emailOptions = ref({});

props.itemsWithContent?.forEach(item => {
    if (item.content) {
        if (props.isCreatePage) {
            const restoredDates = window.restoredPublicationDates || {};
            const savedDate = restoredDates[item.content.id];

            if (savedDate) {
                publicationDates.value[item.content.id] = new Date(savedDate);
            } else {
                publicationDates.value[item.content.id] = null;
            }
        } else {
            let dateValue = null;
            if (item.content.sent_to_portal_at) {
                if (typeof item.content.sent_to_portal_at === 'string') {
                    dateValue = new Date(item.content.sent_to_portal_at);
                } else if (item.content.sent_to_portal_at instanceof Date) {
                    dateValue = item.content.sent_to_portal_at;
                }
            }

            publicationDates.value[item.content.id] = dateValue;
        }
    }
});

if (props.isCreatePage && window.restoredPublicationDates) {
    delete window.restoredPublicationDates;
}

watch(() => props.itemsWithContent, (newItems) => {
    if (newItems) {
        newItems.forEach(item => {
            if (item.content && !publicationDates.value[item.content.id]) {
                if (!props.isCreatePage) {
                    let dateValue = null;
                    if (item.content.sent_to_portal_at) {
                        if (typeof item.content.sent_to_portal_at === 'string') {
                            dateValue = new Date(item.content.sent_to_portal_at);
                        } else if (item.content.sent_to_portal_at instanceof Date) {
                            dateValue = item.content.sent_to_portal_at;
                        }
                    }
                    publicationDates.value[item.content.id] = dateValue;
                }
            }
        });
    }
}, { deep: true, immediate: true });

const itemsCount = computed(() => {
    return props.itemsWithContent?.filter(item => item.content?.confirmed_by_admin_at).length || 0;
});

const sentCount = computed(() => {
    return props.itemsWithContent?.filter(item => item.content?.sent_to_portal_at !== null).length || 0;
});

const confirmedItems = computed(() => {
    if (props.isEditMode) {
        return props.itemsWithContent.filter(item => item.content);
    }
    return props.itemsWithContent.filter(item => item.content?.confirmed_by_admin_at);
});

// Payment warning state
const showPaymentWarning = ref(false);
const pendingPublicationContentId = ref(null);

const confirmPublication = (contentId) => {
    const item = props.itemsWithContent?.find(i => i.content?.id === contentId);
    if (item?.seller_requires_payment && !item?.paid) {
        pendingPublicationContentId.value = contentId;
        showPaymentWarning.value = true;
        return;
    }
    doConfirmPublication(contentId);
};

const proceedWithoutPayment = () => {
    showPaymentWarning.value = false;
    doConfirmPublication(pendingPublicationContentId.value);
    pendingPublicationContentId.value = null;
};

const cancelPaymentWarning = () => {
    showPaymentWarning.value = false;
    pendingPublicationContentId.value = null;
};

const doConfirmPublication = (contentId) => {
    const publicationDate = new Date();

    router.post(
        proxy.route ? proxy.route('order-content.publication.confirm', contentId) : `/admin/order-content/${contentId}/publication`,
        {
            sent_to_portal_at: publicationDate,
            ...(emailOptions.value[contentId] || {}),
        },
        {
            preserveScroll: true,
            preserveState: true,
            onStart: () => showLoading($t('Confirming publication...')),
            onFinish: () => hideLoading(),
            onSuccess: () => {
                emit('refresh-needed');
                emit('publication-confirmed');
            },
            onError: () => hideLoading(),
        }
    );
};

const downloadContent = (contentId) => {
    const url = proxy.route ? proxy.route('order-content.download', contentId) : `/admin/order-content/${contentId}/download`;
    window.open(url, '_blank');
};

const toggleItemExpansion = (itemId) => {
    expandedItems.value[itemId] = !expandedItems.value[itemId];
};

const updatePublicationDate = (contentId, date) => {
    publicationDates.value[contentId] = date;

    if (props.isCreatePage || props.isEditMode) {
        emit('publication-date-changed', { contentId, date });
    }
};
</script>

<template>
    <Card :class="{ 'opacity-100': isEnabled, 'opacity-60': !isEnabled }">
        <template #header>
            <div class="p-6 pb-0">
                <div class="flex items-center gap-3">
                    <div>
                        <h2 class="text-xl font-bold" :class="isEnabled ? 'text-surface-900' : 'text-surface-400'">
                            {{ $t('Request site publication') }}
                        </h2>
                        <p class="text-sm font-medium" :class="(isEnabled && allSent) ? 'text-green-600' : (isEnabled ? 'text-amber-600' : 'text-surface-400')">
                            <span v-if="!isEnabled">{{ $t('Blocked until all article is confirmed') }}</span>
                            <span v-else-if="!hasConfirmedContent">{{ $t('No confirmed content available') }}</span>
                            <span v-else-if="allSent && !isCreatePage">{{ $t('Request sent to site responsible') }}</span>
                            <span v-else-if="allSent && isCreatePage">{{ $t('All publication dates selected') }}</span>
                            <span v-else-if="sentCount === 0">{{ $t('Content not sent yet') }}</span>
                            <span v-else>{{ $t('Content pending publication') }} ({{ sentCount }}/{{ itemsCount }})</span>
                        </p>
                    </div>
                </div>
            </div>
        </template>
        <template #content>
            <div v-if="!isEnabled" class="text-center py-12">
                <i class="pi pi-lock text-6xl text-surface-300 mb-4"></i>
                <h3 class="text-xl font-semibold text-surface-500 mb-2">
                    {{ $t('Article Review Required') }}
                </h3>
                <p class="text-surface-400">
                    {{ $t('Publication will be enabled after all article is confirmed') }}
                </p>
            </div>
            <div v-else-if="!hasConfirmedContent" class="text-center py-12">
                <i class="pi pi-inbox text-6xl text-surface-300 mb-4"></i>
                <h3 class="text-xl font-semibold text-surface-700 mb-2">
                    {{ $t('No confirmed content available') }}
                </h3>
                <p class="text-surface-500">
                    {{ $t('Content must be confirmed before it can be sent to the portal.') }}
                </p>
            </div>
            <div v-else class="space-y-4">
                <!-- Publication Items -->
                <Card v-for="(item, index) in confirmedItems" :key="item.id" class="shadow-sm">
                    <template #header>
                        <div class="p-4 pb-0">
                            <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                                <div class="flex items-center gap-3">
                                    <span class="font-bold text-surface-900">{{ index + 1 }}</span>
                                    <div>
                                        <h3 class="font-semibold text-surface-900">
                                            {{ item.site_url || $t('Site URL not available') }}
                                        </h3>
                                        <p class="text-sm text-surface-600">
                                            {{ item.type_name || '-' }}
                                        </p>
                                    </div>
                                </div>
                                <div v-if="item.content" class="flex items-center gap-3">
                                    <div v-if="item.content.sent_to_portal_at && !isCreatePage && item.publication_recipient" class="text-sm text-surface-600">
                                        <span>{{ $t('Sent to') }}: <span class="font-medium text-surface-800">{{ item.publication_recipient.email_target === 'owner' ? item.publication_recipient.owner_name : item.publication_recipient.seller_name }}</span></span>
                                        <span class="mx-1">{{ $t('at') }}</span>
                                        <span class="font-medium text-surface-800">{{ new Date(item.content.sent_to_portal_at).toLocaleDateString() }}</span>
                                    </div>
                                    <Tag v-if="item.content.sent_to_portal_at && !isCreatePage" :value="$t('Sent to Portal')" severity="success" icon="pi pi-check" />
                                    <Tag v-else-if="publicationDates[item.content.id] && isCreatePage" :value="$t('Date Selected')" severity="success" icon="pi pi-calendar" />
                                    <Tag v-else :value="$t('Content waiting to be send')" severity="warn" icon="pi pi-clock" />

                                    <Button
                                        :icon="expandedItems[item.id] ? 'pi pi-chevron-up' : 'pi pi-chevron-down'"
                                        :label="$t('Show')"
                                        size="small"
                                        severity="secondary"
                                        outlined
                                        @click="toggleItemExpansion(item.id)"
                                    />
                                </div>
                            </div>
                        </div>
                    </template>
                    <template #content>
                        <div v-show="expandedItems[item.id]">
                            <div v-if="item.content" class="space-y-4">
                            <!-- Site Information Section -->
                            <SiteFeaturesInfo v-if="item.site_features" :features="item.site_features" :accepted-anchors="item.offers?.accepted_anchors || item.site_features?.accepted_anchors || 1" />

                            <!-- Anchors Section -->
                            <div v-if="item.content.anchor_name || item.content.anchor_url || item.content.anchor_name_2 || item.content.anchor_url_2 || item.content.anchor_name_3 || item.content.anchor_url_3"
                                 class="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                <h5 class="text-sm font-semibold text-blue-900 mb-3">
                                    <i class="pi pi-link text-blue-700 mr-2"></i>
                                    {{ $t('Anchors') }}
                                </h5>

                                <div class="flex flex-col md:flex-row gap-4">
                                    <!-- Anchor 1 -->
                                    <div v-if="item.content.anchor_name || item.content.anchor_url" class="flex-1">
                                        <div class="space-y-2">
                                            <div>
                                                <label class="block text-xs font-medium text-blue-800 mb-1">
                                                    {{ $t('Anchor Name') }} 1
                                                </label>
                                                <p class="text-sm text-blue-900">{{ item.content.anchor_name || '-' }}</p>
                                            </div>
                                            <div>
                                                <label class="block text-xs font-medium text-blue-800 mb-1">
                                                    {{ $t('Anchor URL') }} 1
                                                </label>
                                                <p class="text-xs text-blue-900 break-all">{{ item.content.anchor_url || '-' }}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Divider -->
                                    <Divider v-if="(item.content.anchor_name || item.content.anchor_url) && (item.content.anchor_name_2 || item.content.anchor_url_2)" layout="vertical" class="hidden md:flex" />

                                    <!-- Anchor 2 -->
                                    <div v-if="item.content.anchor_name_2 || item.content.anchor_url_2" class="flex-1">
                                        <div class="space-y-2">
                                            <div>
                                                <label class="block text-xs font-medium text-blue-800 mb-1">
                                                    {{ $t('Anchor Name') }} 2
                                                </label>
                                                <p class="text-sm text-blue-900">{{ item.content.anchor_name_2 || '-' }}</p>
                                            </div>
                                            <div>
                                                <label class="block text-xs font-medium text-blue-800 mb-1">
                                                    {{ $t('Anchor URL') }} 2
                                                </label>
                                                <p class="text-xs text-blue-900 break-all">{{ item.content.anchor_url_2 || '-' }}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Divider -->
                                    <Divider v-if="(item.content.anchor_name_2 || item.content.anchor_url_2) && (item.content.anchor_name_3 || item.content.anchor_url_3)" layout="vertical" class="hidden md:flex" />

                                    <!-- Anchor 3 -->
                                    <div v-if="item.content.anchor_name_3 || item.content.anchor_url_3" class="flex-1">
                                        <div class="space-y-2">
                                            <div>
                                                <label class="block text-xs font-medium text-blue-800 mb-1">
                                                    {{ $t('Anchor Name') }} 3
                                                </label>
                                                <p class="text-sm text-blue-900">{{ item.content.anchor_name_3 || '-' }}</p>
                                            </div>
                                            <div>
                                                <label class="block text-xs font-medium text-blue-800 mb-1">
                                                    {{ $t('Anchor URL') }} 3
                                                </label>
                                                <p class="text-xs text-blue-900 break-all">{{ item.content.anchor_url_3 || '-' }}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Language, Confirmation and Responsible Details -->
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label class="block text-sm font-medium text-surface-700 mb-1">
                                        {{ $t('Language') }}
                                    </label>
                                    <p class="text-surface-900">{{ item.content.language || '-' }}</p>
                                </div>

                                <div>
                                    <label class="block text-sm font-medium text-surface-700 mb-1">
                                        {{ $t('Confirmed At') }}
                                    </label>
                                    <p class="text-surface-900">
                                        {{ item.content.confirmed_by_admin_at ? new Date(item.content.confirmed_by_admin_at).toLocaleString() : '-' }}
                                    </p>
                                </div>
                            </div>

                            <!-- Seller & Owner Details -->
                            <RecipientContactBlock
                                v-if="item.publication_recipient"
                                :recipient="item.publication_recipient"
                                :read-only="true"
                            />

                            <!-- Content Link or File -->
                            <div v-if="item.content.content_link || item.content.content_link_file" class="p-4 bg-surface-50 rounded-lg border border-surface-200">
                                <label class="block text-sm font-medium text-surface-700 mb-2">
                                    {{ $t('Content') }}
                                </label>

                                <div v-if="item.content.content_link" class="flex items-center gap-2">
                                    <i class="pi pi-link text-primary-500"></i>
                                    <a
                                        :href="item.content.content_link"
                                        target="_blank"
                                        class="text-primary-500 hover:text-primary-600 underline break-all"
                                    >
                                        {{ item.content.content_link }}
                                    </a>
                                </div>

                                <div v-if="item.content.content_link_file" class="flex items-center gap-2">
                                    <i class="pi pi-file text-primary-500"></i>
                                    <Button
                                        :label="$t('Download File')"
                                        icon="pi pi-download"
                                        size="small"
                                        @click="downloadContent(item.content.id)"
                                    />
                                </div>
                            </div>

                            <!-- Observations -->
                            <div v-if="item.observations" class="p-4 bg-amber-50 rounded-lg border border-amber-200">
                                <label class="block text-sm font-medium text-amber-900 mb-2">
                                    <i class="pi pi-info-circle mr-1"></i>
                                    {{ $t('Observations') }}
                                </label>
                                <p class="text-surface-900">{{ item.observations }}</p>
                            </div>

                            <!-- Publication Status (only show for non-edit pages when sent) -->
                            <div v-if="item.content.sent_to_portal_at && !isCreatePage && !isEditMode" class="p-4 bg-green-50 rounded-lg border border-green-200">
                                <div class="flex items-center gap-2 text-green-700">
                                    <i class="pi pi-check-circle text-xl"></i>
                                    <div>
                                        <p class="font-medium">{{ $t('Sent to portal') }}</p>
                                        <p class="text-sm">
                                            {{ new Date(item.content.sent_to_portal_at).toLocaleString() }}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <!-- Email options + Confirm Send -->
                            <div v-if="!hideConfirmButton && !isEditMode && !item.content.sent_to_portal_at" class="space-y-3 pt-2">
                                <PublicationEmailOptions
                                    :recipient="item.publication_recipient"
                                    :content-id="item.content.id"
                                    @change="payload => emailOptions[item.content.id] = payload"
                                />
                                <div class="flex justify-end">
                                    <Button
                                        :label="$t('Confirm Send')"
                                        icon="pi pi-send"
                                        severity="success"
                                        @click="confirmPublication(item.content.id)"
                                        :disabled="item.publication_recipient && !item.publication_recipient.type"
                                    />
                                </div>
                            </div>
                            </div>
                        </div>
                    </template>
                </Card>

                <!-- All Sent Message -->
                <Card v-if="allSent && !isCreatePage && !isEditMode" class="mt-6">
                    <template #content>
                        <div class="text-center py-8">
                            <div class="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                                <i class="pi pi-check text-green-600 text-3xl"></i>
                            </div>
                            <h3 class="text-xl font-semibold text-surface-900 mb-2">
                                {{ $t('Order confirmed!') }}
                            </h3>
                            <p class="text-surface-600">
                                {{ $t('All confirmed content has been sent to the portal. The order is now processing.') }}
                            </p>
                        </div>
                    </template>
                </Card>
            </div>
        </template>
    </Card>

    <Dialog
        v-model:visible="showPaymentWarning"
        modal
        :header="$t('Payment not confirmed')"
        :style="{ width: '480px' }"
        @hide="cancelPaymentWarning"
    >
        <div class="flex items-start gap-4">
            <div class="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                <i class="pi pi-exclamation-triangle text-orange-600 text-xl"></i>
            </div>
            <div>
                <p class="text-surface-700 mb-2">
                    {{ $t('This seller requires payment before publishing, but this item has not been marked as paid.') }}
                </p>
                <p class="text-surface-500 text-sm">
                    {{ $t('You can proceed anyway or go back to mark it as paid first.') }}
                </p>
            </div>
        </div>
        <template #footer>
            <Button
                :label="$t('Cancel')"
                icon="pi pi-times"
                severity="secondary"
                @click="cancelPaymentWarning"
            />
            <Button
                :label="$t('Send anyway')"
                icon="pi pi-send"
                severity="warn"
                @click="proceedWithoutPayment"
            />
        </template>
    </Dialog>
</template>

<style scoped>
.p-tag {
    font-weight: 600;
}
</style>
